/**
 * Live-build refresh (ADR-0024 S3.3).
 *
 * Odino writes to app structure through its OWN backend session (commit_draft /
 * apply_patch), NOT through the builder's mutations — so the open builder's
 * TanStack caches (pages / tables / columns / forms / portal nav …) don't know a
 * change happened and go stale until a manual reload (the gap the user hit:
 * "Contacts 2" created but absent from the page rail until refresh).
 *
 * When a mutating tool's `tool_result` arrives successfully over the SSE stream,
 * `OdinoChat` calls `invalidateBuilderQueries` so the builder beside it refetches
 * and shows the change immediately — no reload. Kept as a pure helper (not inline
 * in the component) so the tool-set and the invalidated roots are unit-testable
 * and documented in one place.
 */
import type { QueryClient } from "@tanstack/react-query";

/**
 * Odino tools that WRITE app structure. A successful result from one of these
 * means the open builder's cached app data is now stale. Read-only tools
 * (get_catalog / get_schema / get_artifact / validate_artifact) are excluded —
 * they change nothing, so invalidating on them would be wasted refetches.
 */
export const MUTATING_ODINO_TOOLS: ReadonlySet<string> = new Set([
  "commit_draft",
  "apply_patch",
  "edit_page",
  "configure_portal",
  "commit_workflow",
  "commit_etl",
]);

/**
 * Query-key roots covering everything the apps builder + app detail render. A
 * structural write can touch any of them (a new page, table, column, form, or a
 * changed portal nav), so we invalidate the whole family — TanStack matches by
 * key PREFIX, so `["apps"]` covers `["apps", appId, "pages"]` etc. Keep in sync
 * with the query keys used under `pages/AppsPage`, `pages/PageBuilderPage` and
 * `components/apps/*`.
 */
export const BUILDER_QUERY_ROOTS: readonly string[] = [
  "apps",
  "tables",
  "table",
  "tables-columns",
  "page",
  "page-preview",
  "page-catalog",
  "forms",
  "portal-nav",
  "portal-branding",
  "runnable-workflows",
  // commit_workflow writes a Workflow row + version — without this root the
  // workflows LIST stayed stale after an Odino-built workflow (the F0 bug:
  // the tool was missing from MUTATING_ODINO_TOOLS AND its list key from here).
  "workflows",
  "workflow",
  "workflow-version",
  // ETL pipelines list (an Odino-built pipeline must appear without a reload).
  "etl",
];

/** Invalidate every builder query family so the open builder refetches live. */
export function invalidateBuilderQueries(qc: QueryClient): void {
  for (const root of BUILDER_QUERY_ROOTS) {
    void qc.invalidateQueries({ queryKey: [root] });
  }
}

/**
 * Convenience guard used by the SSE handler: did a successful, structure-mutating
 * tool just complete? (name present, not an error, and in the mutating set.)
 */
export function isMutatingToolSuccess(name: string | undefined, isError: boolean | undefined): boolean {
  return !!name && !isError && MUTATING_ODINO_TOOLS.has(name);
}
